// src/contexts/BrandingContext.js
import { createMuiTheme, createTheme } from "@material-ui/core";
import React, { createContext, useContext, useEffect, useState } from 'react';

const defaultTheme = createMuiTheme()

// Set default theme branding
window.themeBranding = {
  primary: "#3C9FFA",
  primaryLight: "rgb(99, 178, 251)",
  primaryDark: "rgb(42, 111, 175)",
  secondary: "#00A65A",
  secondaryLight: "rgb(51, 183, 123)",
  secondaryDark: "rgb(0, 116, 62)",
  error: "#f44336",
  warning: "#ff9800",
  info: "#2196f3",
  success: "#4caf50",
  backgroundDefault: "#ECF0F5",
  backgroundPaper: "#FFFFFF",
  textPrimary: "#000000",
  textSecondary: "#555555",
  textLight: "#FFFFFF",
  divider: "rgba(0, 0, 0, 0.12)",
};

const createBrandingTheme = (branding) => {
  const theme = createTheme({
    typography: {
      fontFamily: branding.typography.fontFamily,
      fontSize: branding.typography.fontSize.base,
    },
    palette: {
      primary: {
        main: branding.themeColors.primary,
      },
      secondary: {
        main: branding.themeColors.secondary,
      },
      background: {
        default: branding.themeColors.background,
        paper: branding.themeColors.surface,
      },
      text: {
        primary: branding.themeColors.text.primary,
        secondary: branding.themeColors.text.secondary,
        light: branding.themeColors.text.light,
      },
    },
    shape: {
      borderRadius: 4, // Example shape property
    },
    overrides: {
      MuiToolbar: {
        gutters: {
          [defaultTheme.breakpoints.up("sm")]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    },
  });

  // Set CSS variables from the theme
  setCSSVariablesFromTheme(theme);

  return theme;
};

const setGlobalColors = (theme) => {
  if (!theme) return;

  // Set global colors from the theme palette
  window.themeBranding = {
    primary: theme.palette.primary.main,
    primaryLight: theme.palette.primary.light,
    primaryDark: theme.palette.primary.dark,
    secondary: theme.palette.secondary.main,
    secondaryLight: theme.palette.secondary.light,
    secondaryDark: theme.palette.secondary.dark,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    backgroundDefault: theme.palette.background.default,
    backgroundPaper: theme.palette.background.paper,
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    divider: theme.palette.divider,
  };

};

const setCSSVariablesFromTheme = (theme) => {
  const root = document.documentElement;

  // Set palette colors as CSS variables
  Object.entries(theme.palette).forEach(([key, value]) => {
    if (typeof value === "object") {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (typeof subValue === "string") {
          root.style.setProperty(`--color-${key}-${subKey}`, subValue);
        }
      });
    }
  });

  // Set typography as CSS variables
  Object.entries(theme.typography).forEach(([key, value]) => {
    if (typeof value === "string" || typeof value === "number") {
      root.style.setProperty(`--font-${key}`, value);
    } else if (typeof value === "object") {
      Object.entries(value).forEach(([subKey, subValue]) => {
        root.style.setProperty(`--font-${key}-${subKey}`, subValue);
      });
    }
  });

  // Set other theme properties (e.g., shape, spacing) if needed
  if (theme.shape?.borderRadius) {
    root.style.setProperty("--shape-border-radius", `${theme.shape.borderRadius}px`);
  }
};

const loadBrandingConfig = async () => {
    try {
        const response = await fetch('/branding.json');
      if (!response.ok) {
        throw new Error('Failed to load branding configuration');
      }
      return await response.json();
    } catch (error) {
      throw new Error('Error loading branding configuration: ' + error.message);
    }
};

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};


export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeBranding = async () => {
      try {
        const brandingData = await loadBrandingConfig();
        setBranding(brandingData);
        if (brandingData) {
          const newTheme = createBrandingTheme(brandingData);
          setGlobalColors(newTheme);
          setTheme(newTheme);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeBranding();
  }, []);
  return (
    <BrandingContext.Provider value={{ branding, theme, loading, error }}>
      {children}
    </BrandingContext.Provider>
  );
};